/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { gridSize, colors, borderRadius } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { media } from '@atlassian/bitkit-responsive';

import { ComponentLink } from 'src/components/component-link';
import PullRequestLinkBase from 'src/components/pull-request-link';
import { overflowEllipsis } from 'src/styles/mixins';

const link = () => `
  &,
  &:hover {
    color: ${token('color.text', colors.N800)};
  }
`;

type RowProps = {
  isMainBranch: boolean;
  isFocused: boolean;
};

const rowBackgroundColor = ({ isMainBranch, isFocused }: RowProps) => {
  if (isFocused) {
    return token('elevation.surface.hovered', colors.N20);
  } else if (isMainBranch) {
    return token('elevation.surface.sunken', colors.N10);
  } else {
    return token('elevation.surface', 'initial');
  }
};

export const TableRow = styled.tr<RowProps>`
  background-color: ${props => rowBackgroundColor(props)};
  box-shadow: ${({ isMainBranch }) =>
    isMainBranch
      ? `-${gridSize() / 2}px 0 0 0 ${token(
          'elevation.surface.sunken',
          colors.N10
        )},
          ${gridSize() / 2}px 0 0 0 ${token(
          'elevation.surface.sunken',
          colors.N10
        )}`
      : 'none'};
  border-radius: ${({ isMainBranch }) =>
    isMainBranch ? `${borderRadius()}px` : 0};
  &:hover,
  &:focus {
    background-color: ${({ isMainBranch }) =>
      isMainBranch
        ? token('elevation.surface.sunken', colors.N10)
        : token('elevation.surface.hovered', colors.N20)};
    box-shadow: ${({ isMainBranch }) =>
      isMainBranch
        ? `-${gridSize() / 2}px 0 0 0 ${token(
            'elevation.surface.sunken',
            colors.N10
          )}, ${gridSize() / 2}px 0 0 0 ${token(
            'elevation.surface.sunken',
            colors.N10
          )}`
        : `-${gridSize() / 2}px 0 0 0 ${token(
            'elevation.surface.hovered',
            colors.N20
          )}, ${gridSize() / 2}px 0 0 0 ${token(
            'elevation.surface.hovered',
            colors.N20
          )}`};

    border-radius: ${borderRadius()}px;
  }
`;

export const TableCell = styled.td({
  padding: `${gridSize()}px`,
});
TableCell.displayName = 'td';

export const BranchName = styled.div({
  display: 'flex',
  alignItems: 'center',
  width: '93%',
  '> div': {
    maxWidth: '100%',
  },
});

export const BranchLink = styled(ComponentLink)(link(), overflowEllipsis(), {
  display: 'block',
});

export const BranchTarget = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  '> :first-child': {
    height: '28px',
    marginRight: `${gridSize() / 2}px`,
  },
});

export const TargetLink = styled.a(link());

export const BranchPullRequest = styled.div({
  display: 'flex',
  '> div': {
    flex: 1,
    '&:first-child': {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: `${gridSize() / 4}px`,
      paddingRight: `${gridSize() / 2}px`,
    },
    '&:last-child': {
      paddingTop: '1px',
    },
  },
});

export const PullRequestLink = styled(PullRequestLinkBase)(link());

export const BranchDivergenceTableCell = styled(TableCell)(
  media.upToLarge(`
    display: none;
  `)
);

export const TargetTableCell = styled(TableCell)(
  media.upToLarge(`
    display: none;
  `)
);

export const PullRequestTableCell = styled(TableCell)(
  media.upToLarge(`
    display: none;
  `)
);
